import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { Grid, Typography, Divider, Link, Checkbox } from '@material-ui/core';

// CONSTANTS
import { ROUTE_DISCOVER_APP } from '@/constants/routes';
import { LOCATIONS_SUBSCIPTION_FORM_PAGE } from '@/constants/locations';

// UTILITY
import { formatPrice } from '@/utility/currency';
import { getTotalPrice } from '@/utility/subscription';
import getLink from '@/utility/routes';

// COMPONENTS
import Loader from '@/components/Loader';
import RouterLink from '@/components/Link';
import MBLogoIcon from '@/components/Icons/MBLogoIcon';

// HELP
import BillingDetails from './BillingDetails';

// STYLES
import { SSubscriptionPlanInfo, SpecialOffer } from './styles';

function SubscriptionPlanInfo(props) {
  const {
    dictionary,
    cards,
    showEditPlan,
    isLastStep,
    onEditPlanClick,
    preview,
    previewLoading,
    onSpecOfferClick,
    specOfferValue,
  } = props;
  const [t] = useTranslation();

  const {
    app,
    selectedProduct,
    selectedAddons,
    selectedPlan: plan,
    subscription: { registration_meta, merchant },
    active_subscription,
    subscription_end_date,
  } = dictionary;

  const hasPreviewData = Object.keys(preview).length;
  const endDate = subscription_end_date || active_subscription?.active_until;
  const hasPayment = !!registration_meta.payment;
  const userCards = cards.filter(card => card.type === 'card');
  const defaultCard = userCards.find(card => card.default) || userCards[userCards.length - 1];
  const showBillingDetails = hasPayment && defaultCard && endDate;
  const planPrice = preview.extra_cost ? +plan.price + +preview.extra_cost : plan.price;
  const totalPrice = dictionary.totalPrice || getTotalPrice(plan, selectedAddons);
  const appLink = getLink(ROUTE_DISCOVER_APP, {
    slug: app.unique_slug,
  });
  const merchantType = merchant?.slug;
  const isSlingType = merchantType === 'sling-tv' || merchantType === 'sling-latino';
  const isDeviceProductType = selectedProduct.type === 'device_product';
  const hasDiscount = !!plan.discount_price;

  const getPrice = (price, interval, intervalProp = {}) => {
    return price && Math.ceil(price) !== 0 ? (
      <Typography component="span" {...intervalProp}>
        ${formatPrice(+price)}
        {!!interval && `/${interval}`}
      </Typography>
    ) : (
      t('common.free')
    );
  };

  const getAddonsContent = addons => {
    return addons.map(addon => {
      const isIncluded = !!addon.included_in;

      return (
        <div
          className={clsx('desc-wrapper__info', isIncluded && 'desc-wrapper__info--has-included')}
          key={addon.id}
        >
          <Typography className="addons__label">{addon.name}</Typography>
          <Typography className={clsx('p4 price', isIncluded && 'price--has-included')}>
            <span className="price__price">
              {getPrice(addon.plans[0].price, addon.plans[0].interval, {
                className: 'p5',
                color: 'secondary',
              })}
            </span>
            {isIncluded && (
              <span className="price__desc p5">
                {t('common.includedIn', { name: addon.included_in })}
              </span>
            )}
          </Typography>
        </div>
      );
    });
  };

  const getTax = () => {
    let tax = t('subscription.tax.defaultText');

    if (isLastStep && hasPreviewData) {
      tax = (
        <>
          ${formatPrice(+preview.tax)}
          {!!plan.interval && `/${plan.interval}`}
        </>
      );
    }

    if (isDeviceProductType && merchantType === 'roku_streaming_device') tax = t('common.free');

    return tax;
  };

  return (
    <SSubscriptionPlanInfo>
      <div className="plan-info">
        <Grid container spacing={4}>
          <Grid item xs={12} md={12}>
            <div className="main-info">
              <RouterLink to={appLink} location={LOCATIONS_SUBSCIPTION_FORM_PAGE} target="_blank">
                <div className="img-wrapper">
                  <img className="product-logo" src={app.thumbnail} alt="product-logo" />
                </div>
              </RouterLink>
              <Typography className="plan-title" component="h5">
                {app.name}
              </Typography>
            </div>
          </Grid>
          <Grid item xs={12} md={12}>
            <div className="desc-wrapper">
              <div className="desc-wrapper__item">
                <Typography className="p3 m-b-20">
                  {isDeviceProductType ? t('subscription.device') : `${t('subscription.title')}:`}
                </Typography>
                <div
                  className={clsx(
                    'desc-wrapper__info',
                    hasDiscount && 'desc-wrapper__info--has-discount',
                  )}
                >
                  <Typography className="plan-info__label">{selectedProduct.name}</Typography>
                  <Typography
                    className={clsx(
                      'price plan-info__label-2',
                      hasDiscount && 'price--has-discount',
                    )}
                  >
                    <span className="price__price">
                      {getPrice(planPrice, plan.interval, {
                        className: 'plan-info__label-2',
                      })}
                    </span>
                  </Typography>
                </div>
                {hasDiscount && (
                  <Typography
                    className="price__discount plan-info__label-2 m-t-10 text-color--purple"
                    align="right"
                  >
                    {getPrice(plan.discount_price, null, {
                      className: 'plan-info__label-2 text-regular',
                    })}{' '}
                    {plan.discount_description}
                  </Typography>
                )}
              </div>
              {Boolean(selectedAddons?.length) && (
                <div className="desc-wrapper__item addons">
                  <Typography className="plan-info__label-2 m-b-5">
                    {t('common.addOns')}:
                  </Typography>
                  {getAddonsContent(selectedAddons)}
                </div>
              )}
              {specOfferValue === 'yes' && (
                <div className="desc-wrapper__item">
                  <div className="desc-wrapper__info">
                    <Typography className="plan-info__label-2 m-b-5">
                      {t('subscription.slingOffer.title')}:
                    </Typography>
                    <Typography className="plan-info__label-2">
                      {getPrice(0, '', {
                        className: 'p5',
                        color: 'secondary',
                      })}
                    </Typography>
                  </div>
                </div>
              )}
              {isDeviceProductType && (
                <div className="desc-wrapper__item desc-wrapper__item--one-line desc-wrapper__item--without-indent">
                  <Typography className="plan-info__label-2">
                    {t('subscription.quantity')}:
                  </Typography>
                  <div className="desc-wrapper__info">
                    <Typography className="plan-info__label-2">1</Typography>
                  </div>
                </div>
              )}
              <div
                className={clsx(
                  'desc-wrapper__item desc-wrapper__item--tax',
                  isDeviceProductType && 'desc-wrapper__item--without-indent',
                )}
              >
                <Typography className="plan-info__label-2">
                  {t('subscription.tax.title')}:
                </Typography>
                <div className="desc-wrapper__info">
                  <Typography className="plan-info__label-2">{getTax()}</Typography>
                </div>
              </div>
              {isDeviceProductType && (
                <div className="desc-wrapper__item desc-wrapper__item--one-line desc-wrapper__item">
                  <Typography className="plan-info__label-2">
                    {t('subscription.shipping')}:
                  </Typography>
                  <div className="desc-wrapper__info">
                    <Typography className="plan-info__label-2">{t('common.free')}</Typography>
                  </div>
                </div>
              )}
            </div>
          </Grid>
        </Grid>
        {showBillingDetails && (
          <>
            <Divider />
            <BillingDetails className="billing-details" card={defaultCard} endDate={endDate} />
          </>
        )}
        <Divider />
        <div
          className={clsx(
            'total-price-wrapper',
            hasDiscount && 'total-price-wrapper--has-discount',
          )}
        >
          <Typography className="plan-info__label">
            {t(`common.${isLastStep ? 'total' : 'price'}`)}:
          </Typography>
          <Typography className="total-price plan-info__label-2">
            {getPrice(totalPrice, hasDiscount ? null : plan.interval, {
              id: 'total-price',
              className: 'plan-info__label-2 text-medium',
            })}
            {hasDiscount && (
              <span className="discount plan-info__label-2 m-t-10 text-color--error">
                {t('subscription.discount_profit', {
                  price: formatPrice(planPrice - plan.discount_price),
                })}
                {!!plan.discount_percentage && `(${plan.discount_percentage}%)`}
              </span>
            )}
          </Typography>
        </div>
        {!!selectedProduct.trial_days && (
          <div className="desc-wrapper__item m-t-10">
            <Typography className="plan-info__label-2">
              {t('subscription.trial_info', {
                trial_days: selectedProduct?.trial_days || '',
                charge_date: selectedProduct?.charge_date
                  ? new Date(selectedProduct.charge_date).toLocaleDateString()
                  : '',
              })}
            </Typography>
          </div>
        )}
        {hasDiscount && !!preview.monthly_total && (
          <>
            <Divider />
            <Typography className="p5 m-b-15">
              {t('subscription.discount_info', {
                total_price: `$${formatPrice(+totalPrice)}`,
                price: `$${formatPrice(+preview.monthly_total)}`,
              })}
            </Typography>
          </>
        )}
        {showEditPlan && (
          <div className="edit-plan">
            {!(hasDiscount && !!preview.monthly_total) && <Divider />}
            <Link component="button" color="secondary" underline="always" onClick={onEditPlanClick}>
              {t('buttons.editPlan')}
            </Link>
          </div>
        )}
      </div>
      <div className="plan-footer">
        {t('common.poweredBy')}&nbsp;
        <MBLogoIcon />
      </div>
      {/* false set that temporarily hides spec offer logic */}
      {isSlingType && false && (
        <SpecialOffer className="special-offer">
          <Typography className="special-offer__header p3" align="center">
            {t('subscription.specialOffer.title')}
          </Typography>
          <div className="special-offer__content">
            <Checkbox
              className="m-r-10"
              name="special-offer"
              onChange={onSpecOfferClick}
              checked={specOfferValue === 'yes'}
            />
            <Typography className="p3 noselect">{t('subscription.specialOffer.text')}</Typography>
          </div>
        </SpecialOffer>
      )}
      <Loader active={previewLoading} />
    </SSubscriptionPlanInfo>
  );
}

SubscriptionPlanInfo.defaultProps = {
  showEditPlan: false,
  onEditPlanClick: () => {},
  isLastStep: false,
  onSpecOfferClick: () => {},
};

SubscriptionPlanInfo.propTypes = {
  showEditPlan: PropTypes.bool,
  isLastStep: PropTypes.bool,
  onEditPlanClick: PropTypes.func,
  cards: PropTypes.array.isRequired,
  dictionary: PropTypes.object.isRequired,
  preview: PropTypes.object.isRequired,
  previewLoading: PropTypes.bool.isRequired,
  onSpecOfferClick: PropTypes.func,
  specOfferValue: PropTypes.string,
};

export default SubscriptionPlanInfo;
